import type { WsIncoming, WsOutgoing } from './types.js';

export type WsClientOptions = {
  url: string;
  onMessage: (msg: WsIncoming) => void;
  onStatusChange: (
    status: 'connecting' | 'connected' | 'reconnecting' | 'failed' | 'closed',
  ) => void;
  maxRetries?: number;
};

export type WsClient = {
  send(out: WsOutgoing): boolean;
  close(): void;
  /**
   * Force une reconnexion immédiate (backoff/compteur de tentatives remis à
   * zéro) — utilisé quand l'onglet revient au premier plan (visibilitychange,
   * pageshow, online) : iOS Safari gèle/ferme la WS en arrière-plan sans que
   * l'app le sache toujours immédiatement, et le plafond de tentatives peut
   * avoir été atteint (statut 'failed') pendant que l'onglet était caché.
   * No-op si une connexion est déjà ouverte ou en cours d'établissement.
   */
  reconnectNow(): void;
};

export function createWsClient(opts: WsClientOptions): WsClient {
  const maxRetries = opts.maxRetries ?? 3;
  let socket: WebSocket | null = null;
  let retries = 0;
  let manualClose = false;
  let reconnectTimer: number | null = null;

  function connect(): void {
    if (manualClose) return;
    opts.onStatusChange(retries === 0 ? 'connecting' : 'reconnecting');
    socket = new WebSocket(opts.url);

    socket.addEventListener('open', () => {
      retries = 0;
      opts.onStatusChange('connected');
    });

    socket.addEventListener('message', (ev) => {
      try {
        const parsed = JSON.parse(ev.data) as WsIncoming;
        opts.onMessage(parsed);
      } catch {
        /* ignore malformed */
      }
    });

    socket.addEventListener('close', () => {
      if (manualClose) {
        opts.onStatusChange('closed');
        return;
      }
      if (retries >= maxRetries) {
        opts.onStatusChange('failed');
        return;
      }
      const baseDelay = Math.min(30_000, 1000 * Math.pow(2, retries));
      const jitter = Math.random() * 0.4 - 0.2; // ±20%
      const delay = Math.max(500, Math.floor(baseDelay * (1 + jitter)));
      retries++;
      reconnectTimer = window.setTimeout(connect, delay);
    });

    socket.addEventListener('error', () => {
      socket?.close();
    });
  }

  function send(out: WsOutgoing): boolean {
    if (socket?.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify(out));
      return true;
    }
    return false;
  }

  function close(): void {
    manualClose = true;
    if (reconnectTimer !== null) {
      window.clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    socket?.close();
  }

  function reconnectNow(): void {
    if (manualClose) return;
    if (reconnectTimer !== null) {
      window.clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    retries = 0;
    const state = socket?.readyState;
    if (state === WebSocket.OPEN || state === WebSocket.CONNECTING) return;
    connect();
  }

  connect();
  return { send, close, reconnectNow };
}
